import { useEffect, useState } from "react";
import { Trash2, Mail, Phone } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["new", "reviewed", "approved", "rejected"];
const STATUS_BG = {
    new: "Нова",
    reviewed: "Прегледана",
    approved: "Одобрена",
    rejected: "Отказана",
};

function StatusSelector({ value, onChange, testid }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="bg-surface2 border border-line rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gold/60"
            data-testid={testid}
        >
            {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_BG[s] || s}</option>
            ))}
        </select>
    );
}

function makeRequestsPage({ endpoint, title, subtitle, testIdPrefix, renderDetails }) {
    return function RequestsPage() {
        const [items, setItems] = useState([]);
        const [loading, setLoading] = useState(true);

        const load = () => {
            setLoading(true);
            api.get(`/admin/${endpoint}`).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
        };
        useEffect(() => { load(); }, []);

        const onStatus = async (id, status) => {
            await api.put(`/admin/${endpoint}/${id}/status`, null, { params: { status } });
            load();
        };
        const onDelete = async (id) => {
            if (!window.confirm("Изтриване на заявката?")) return;
            await api.delete(`/admin/${endpoint}/${id}`);
            load();
        };

        return (
            <div data-testid={`${testIdPrefix}-page`}>
                <h1 className="font-display text-4xl tracking-tightest mb-2">{title}</h1>
                <p className="text-ink-muted mb-10">{subtitle}</p>
                {loading ? (
                    <div className="text-ink-muted">Зареждане…</div>
                ) : items.length === 0 ? (
                    <div className="text-ink-muted p-8 bg-surface border border-line rounded-2xl text-center">
                        Няма получени заявки.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {items.map((r) => (
                            <div key={r.id} className="bg-surface border border-line rounded-2xl p-6" data-testid={`${testIdPrefix}-${r.id}`}>
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="font-medium text-lg">{r.name}</div>
                                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-ink-muted">
                                            <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-gold"><Mail size={12} /> {r.email}</a>
                                            <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-gold"><Phone size={12} /> {r.phone}</a>
                                            <span>{new Date(r.created_at).toLocaleString("bg-BG")}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <StatusSelector value={r.status} onChange={(s) => onStatus(r.id, s)} testid={`${testIdPrefix}-status-${r.id}`} />
                                        <button onClick={() => onDelete(r.id)} className="w-8 h-8 rounded-lg border border-line hover:border-red-500 hover:text-red-400 flex items-center justify-center" data-testid={`${testIdPrefix}-delete-${r.id}`}>
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-4">{renderDetails(r)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };
}

export const AdminFinancePage = makeRequestsPage({
    endpoint: "finance-requests",
    title: "Финансиране",
    subtitle: "Заявки за финансиране",
    testIdPrefix: "admin-finance",
    renderDetails: (r) => (
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {r.car_label && <Info label="Желан автомобил" value={r.car_label} />}
            {r.down_payment != null && <Info label="Първоначална вноска" value={`${r.down_payment} EUR`} />}
            {r.months != null && <Info label="Срок" value={`${r.months} месеца`} />}
            {r.monthly_income != null && <Info label="Месечен доход" value={`${r.monthly_income} EUR`} />}
            {r.notes && <div className="sm:col-span-2 text-ink-muted text-sm pt-2 border-t border-line">{r.notes}</div>}
        </div>
    ),
});

export const AdminTradeInPage = makeRequestsPage({
    endpoint: "tradein-requests",
    title: "Бартер",
    subtitle: "Заявки за замяна на автомобил",
    testIdPrefix: "admin-tradein",
    renderDetails: (r) => (
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Info label="Настоящ автомобил" value={`${r.current_brand} ${r.current_model} (${r.current_year})`} />
            <Info label="Пробег" value={`${r.current_mileage} км`} />
            <Info label="Състояние" value={r.current_condition} />
            {r.desired_car_label && <Info label="Желан" value={r.desired_car_label} />}
            {r.notes && <div className="sm:col-span-2 text-ink-muted text-sm pt-2 border-t border-line">{r.notes}</div>}
        </div>
    ),
});

function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-ink-subtle">{label}</div>
            <div className="text-ink/90 mt-0.5">{value}</div>
        </div>
    );
}
